import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { canEditSkills, isAdmin } from '../../shared/catalog.js'
import { Icon } from '../components/icons/Icon.jsx'
import { SystemCard } from '../components/library/SystemCard.jsx'
import { SystemDetail } from '../components/library/SystemDetail.jsx'
import { SystemFormDialog } from '../components/library/SystemFormDialog.jsx'
import { UploadDialog } from '../components/library/UploadDialog.jsx'
import l from '../components/library/library.module.css'
import { useMe } from '../lib/queries/session.js'
import { useSystems } from '../lib/queries/systems.js'
import p from './pages.module.css'

/** /library: every system as a grid card, each with an Edit button. */
export function LibraryIndexPage() {
  const { data: systems = [], isPending } = useSystems()
  return <LibraryShell systems={systems} loading={isPending} grid />
}

export function LibraryPage() {
  const { slug } = useParams({ strict: false })
  const search = useSearch({ strict: false })
  const { data: systems = [], isPending } = useSystems()
  if (isPending) return <LibraryShell loading />
  return <LibraryShell systems={systems} slug={slug} search={search} />
}

function LibraryShell({ systems = [], slug, search = {}, loading = false, grid = false }) {
  const navigate = useNavigate()
  const { user } = useMe()
  const canEdit = canEditSkills(user)
  const [adding, setAdding] = useState(false)
  const [uploadFor, setUploadFor] = useState(null)
  const selected = systems.find((s) => s.slug === slug)

  return (
    <div className={p.page}>
      <div className={p.pageHead}>
        <div className={p.pageTitles}>
          <h1 className={p.pageTitle}>System library</h1>
          <p className={p.pageLead}>
            Each system's skill files are what the assistant reads when it answers a question.
          </p>
        </div>
        {canEdit ? (
          <button type="button" className={`btn btn-dark ${p.bigButton}`} onClick={() => setAdding(true)}>
            <Icon name="plus" size={16} strokeWidth={1.7} />
            Add system
          </button>
        ) : null}
      </div>

      {!grid ? (
        <Link to="/library" className={l.backLink}>
          <Icon name="arrow-right" size={14} strokeWidth={1.6} style={{ transform: 'rotate(180deg)' }} />
          All systems
        </Link>
      ) : null}

      <div className={grid ? undefined : l.body}>
        <div className={grid ? l.grid : l.list}>
          {loading
            ? [0, 1, 2].map((i) => <div key={i} className="skeleton" style={{ height: 112, borderRadius: 14 }} />)
            : systems.map((system) => (
                <SystemCard
                  key={system.slug}
                  system={system}
                  selected={system.slug === slug}
                  canEdit={canEdit}
                  showEdit={grid}
                  onAddSkill={() => setUploadFor(system)}
                />
              ))}
          {canEdit ? (
            <button type="button" className={l.addDashed} onClick={() => setAdding(true)}>
              <Icon name="plus" size={16} strokeWidth={1.7} />
              Add a system
            </button>
          ) : null}
        </div>
        {grid ? null : selected ? (
          <SystemDetail key={selected.slug} system={selected} search={search} canEdit={canEdit} canDelete={isAdmin(user)} />
        ) : !loading && slug ? (
          <div className={l.detail}>
            <p className={l.description}>No system called “{slug}”.</p>
          </div>
        ) : null}
      </div>

      <SystemFormDialog
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={(newSlug) => navigate({ to: '/library/$slug', params: { slug: newSlug } })}
      />
      {uploadFor ? <UploadDialog system={uploadFor} open onClose={() => setUploadFor(null)} /> : null}
    </div>
  )
}
